import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileText, Download, Loader2, LifeBuoy } from "lucide-react";
import { get, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState } from "@/components/common/States";
import { ROLES } from "@/lib/permissions";
import { cn } from "@/lib/utils";

const telecharger = async (doc) => {
  const r = await api.get(`/ext/docs/${doc.cle}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.fichier;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const DocRow = ({ doc, mine }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await telecharger(doc);
      toast.success(`${doc.titre} téléchargé`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`doc-${doc.cle}`}
      className={cn("panel relative p-3 pl-4 flex flex-col sm:flex-row sm:items-center gap-3", mine && "border-primary/50 bg-accent/20")}>
      <span className={cn("status-stripe", mine ? "bg-primary" : "bg-slate-600")} />
      <FileText className="h-5 w-5 text-primary shrink-0" />
      <div className="min-w-0 sm:flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{doc.titre}</span>
          <span className="eyebrow border border-border rounded-sm px-1.5 py-0.5">{doc.pour}</span>
          {mine && <span className="eyebrow border border-primary/60 text-primary rounded-sm px-1.5 py-0.5">Mon rôle</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{doc.resume}</p>
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <div className="font-mono text-[11px] text-muted-foreground shrink-0">
          {doc.disponible ? `DOCX · ${doc.taille_ko} Ko` : "Indisponible"}
        </div>
        <button onClick={go} disabled={busy || !doc.disponible} data-testid={`doc-download-${doc.cle}`}
          className="h-8 px-3 border border-border rounded-sm text-xs font-semibold flex items-center gap-1.5 hover:bg-muted disabled:opacity-40">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Télécharger
        </button>
      </div>
    </div>
  );
};

export default function AidePage() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ["docs"], queryFn: () => get("/ext/docs") });
  const mine = user.role === "operateur" ? ["operateur", "fiches"] : user.role === "superviseur" ? ["planificateur"] : [user.role];

  return (
    <div data-testid="aide-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Aide" }]} title="Aide & manuels"
        subtitle={`Documentation ProdTrack — ${ROLES[user.role]?.label || user.role}`} />
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : (
        <>
          <div className="panel px-3 py-2 flex items-start gap-2 text-xs border-l-2 border-l-primary" data-testid="aide-hint">
            <LifeBuoy className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span>Les manuels utilisent de vraies captures d'écran ProdTrack. Les <b>fiches visuelles</b> sont
              conçues pour être imprimées en A4 et affichées près des postes de travail.</span>
          </div>
          <div className="space-y-2" data-testid="docs-list">
            {(q.data || []).map((d) => <DocRow key={d.cle} doc={d} mine={mine.includes(d.cle)} />)}
          </div>
        </>
      )}
    </div>
  );
}
